import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

type SubscriptionTier = 'free' | 'premium' | 'team';

interface Subscription {
  tier: SubscriptionTier;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  loading: boolean;
  isPremium: boolean;
  isTeam: boolean;
  isTeamMember: boolean;
  teamId: string | null;
  refetch: () => Promise<void>;
}

const defaultSubscription: Subscription = {
  tier: 'free',
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  currentPeriodStart: null,
  currentPeriodEnd: null,
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchInProgress = useRef(false);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(defaultSubscription);
      setTeamId(null);
      setLoading(false);
      return;
    }

    // Prevent duplicate fetches
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;

    try {
      // Call check-subscription edge function to sync Stripe status
      // This may fail if session isn't fully established yet, which is ok
      try {
        const { data: stripeData, error: stripeError } = await supabase.functions.invoke('check-subscription');
        
        if (stripeError) {
          // Don't log as error if it's an auth issue during initial load
          console.log('Stripe subscription check skipped:', stripeError.message);
        } else if (stripeData) {
          console.log('Stripe subscription check result:', stripeData);
        }
      } catch (e) {
        // Silently ignore - session may not be ready yet
        console.log('Stripe check skipped - session initializing');
      }

      // Now fetch the updated subscription from database
      const [subResult, teamResult] = await Promise.all([
        supabase
          .from('user_subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('team_members')
          .select('team_id')
          .eq('user_id', user.id)
          .maybeSingle()
      ]);

      // Handle subscription data
      if (subResult.error && subResult.error.code !== 'PGRST116') {
        console.error('Error fetching subscription:', subResult.error);
      }

      if (subResult.data) {
        setSubscription({
          tier: subResult.data.tier as SubscriptionTier,
          stripeCustomerId: subResult.data.stripe_customer_id,
          stripeSubscriptionId: subResult.data.stripe_subscription_id,
          currentPeriodStart: subResult.data.current_period_start,
          currentPeriodEnd: subResult.data.current_period_end,
        });
      } else {
        setSubscription(defaultSubscription);
      }

      // Handle team data
      if (teamResult.error && teamResult.error.code !== 'PGRST116') {
        console.error('Error fetching team membership:', teamResult.error);
      }
      setTeamId(teamResult.data?.team_id || null);
    } catch (error) {
      console.error('Error in fetchSubscription:', error);
      setSubscription(defaultSubscription);
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const isPremium = subscription?.tier === 'premium' || subscription?.tier === 'team';
  const isTeam = subscription?.tier === 'team';
  const isTeamMember = teamId !== null;

  return (
    <SubscriptionContext.Provider 
      value={{ 
        subscription, 
        loading, 
        isPremium, 
        isTeam,
        isTeamMember, 
        teamId,
        refetch: fetchSubscription 
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
